import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import { getUserApi } from "../api/endpoints/generalAPI";
import { GetUserResponse } from "../api/types/userTypes";

const USER_KEY = "User";
const LAST_UPDATED_KEY = "User_LastUpdated";

type UserContextType = {
    user: GetUserResponse | null;
    setUser: (newUser: GetUserResponse) => void;
    isUserStatusLoaded: boolean;
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<GetUserResponse | null>(null);
    const [isUserStatusLoaded, setIsUserStatusLoaded] = useState<boolean>(false);

    useEffect(() => {
        const initUser = async () => {
            const data = await getUserApi();
            secureLocalStorage.setItem(USER_KEY, data);
            secureLocalStorage.setItem(LAST_UPDATED_KEY, new Date().getTime().toString());
            setUser(data);
            setIsUserStatusLoaded(true);
        };

        const checkAndUpdateUser = async () => {
            setIsUserStatusLoaded(false);
            try {
                const lastUpdated = secureLocalStorage.getItem(LAST_UPDATED_KEY);
                const currentTime = new Date().getTime();
                const difference = currentTime - (lastUpdated ? parseInt(lastUpdated.toString()) : 0);
                const oneDay = 86400000; // 24 hours in milliseconds
                //Check if the last update was more than 24 hours ago
                if (difference > oneDay || !lastUpdated) {
                    await initUser();
                } else {
                    const userData = secureLocalStorage.getItem(USER_KEY);

                    if (userData) {
                        setUser(userData as GetUserResponse);
                        setIsUserStatusLoaded(true);
                    } else {
                        await initUser();
                    }
                }
            } catch {
                setUser(null);
            }
        };

        checkAndUpdateUser();
    }, []);

    return <UserContext.Provider value={{ user, setUser, isUserStatusLoaded }}>{children}</UserContext.Provider>;
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) throw new Error("useUser must be used within a UserProvider");
    return context;
};
